/**
 * Simple modal component (no external dependency)
 * Props:
 * - open: boolean
 * - title: string
 * - onClose: () => void
 * - onConfirm?: () => void
 * - confirmText?: string (default: "Save")
 * - children: ReactNode
 */
import { useEffect, useRef } from 'react';

const Modal = ({
  open,
  title,
  onClose,
  onConfirm,
  confirmText = 'Save',
  children,
  confirmDisabled = false,
  confirmLoading = false
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
      if (e.key === 'Enter') {
        if (onConfirm) {
          e.preventDefault();
          onConfirm();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    // autofocus first input if present
    const t = setTimeout(() => {
      inputRef.current?.focus?.();
    }, 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, onClose, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative mx-4 w-full max-w-sm rounded-xl border bg-white p-4 shadow-xl dark:bg-gray-900 dark:border-gray-700">
        {title && (
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        )}
        <div className="mt-2">{typeof children === 'function' ? children({ inputRef }) : children}</div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          {onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirmDisabled || confirmLoading}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {confirmLoading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
